<template>
  <div
    :class="cardClasses"
    :data-test="cardName"
    :data-card-id="id"
  >
    <div class="card-body">
      <div v-if="editable" class="d-flex align-items-center mb-3">
        <AppIcon v-if="icon" :name="icon" class="blue-text" />
        <input
          v-model="node.config.title"
          type="text"
          class="form-control flex-grow border-0 font-weight-bold"
          placeholder="Card title"
          @input="$emit('formUpdated')"
          @change="$emit('update', { property: 'title', value: $event.target.value })"
        />
        <slot name="after-title" />
      </div>
      <h5
        v-else-if="!hiddenHeader"
        class="card-title mb-3 d-flex align-items-center"
      >
        <AppIcon :name="icon" class="blue-text" />
        <span
          class="small ml-3 font-weight-bold mr-2"
          :data-test="'display_' + title"
        >{{ title }}</span>
        <AppStatusDot v-if="cardStatus" :status="cardStatus" />
        <slot name="after-title" />
      </h5>
      <AppAlert
        v-if="validation && newVuelidate && $v.$invalid"
        variant="danger"
        class="d-flex align-items-center"
      >
        <AppIcon name="error" class="pr-2" />
        <div>
          <p class="mb-0">
            <span>{{ error }}</span>
            <template v-if="useOwnErrors">
              <strong
                v-for="ownError in $v.$errors"
                :key="ownError.$property"
              >
                {{ ownError.$message }}
              </strong>
            </template>
          </p>
        </div>
      </AppAlert>
      <slot />
    </div>
  </div>
</template>

<script>
import useVuelidate from '@vuelidate/core'
import { toRefs, inject, computed } from '@vue/composition-api'
import { DETERMINE_CARD_STATUS_INJECTION_KEY } from '../DynamicForms/useFieldStatus'

export default {
  inheritAttrs: false,
  props: {
    title: {
      type: String,
      default: '',
    },
    id: {
      type: String,
      default: '',
    },
    node: {
      type: Object,
      required: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    hiddenHeader: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    validation: {
      type: Boolean,
      default: false,
    },
    newVuelidate: {
      type: Boolean,
      default: false,
    },
    error: {
      type: String,
      default: 'Missing or invalid fields.',
    },
    // At some point this can be refactored so that e.g parent passes errors with slot or prop,
    // but for now we can't, as we're not allowed to use `$v` in SchemaForm's `:layout`
    // (and we very often need some info from $v to display error, e.g Professors.vue)
    useOwnErrors: {
      type: Boolean,
      default: false,
    },
  },
  setup (props) {
    const { validation, newVuelidate, id } = toRefs(props)

    const $v = validation.value && newVuelidate.value
      ? useVuelidate()
      : null

    const getCardStatus = inject(DETERMINE_CARD_STATUS_INJECTION_KEY, () => {})

    const cardStatus = computed(() => getCardStatus(id.value))

    return { $v, cardStatus }
  },
  computed: {
    cardName () {
      return (this.title && this.title.split(' ').join('-')) || ''
    },
    cardClasses () {
      return [
        'card',
        ...(this.validation && this.newVuelidate
          ? [ 'border', `border-${this.$v.$invalid ? 'danger' : 'transparent'}` ]
          : []),
      ].join(' ')
    },
  },
}
</script>
